import axios from '@axios'

/** What the uploaders share: the request, the checks before it, and how a file and an error are described. */

const IMAGE_RE = /\.(jpe?g|png|gif|webp|avif|svg|bmp)(\?.*)?$/i

export const isImageName = name => IMAGE_RE.test(String(name || ''))

export const fileExtension = name => {
  const clean = String(name || '').split('?')[0]
  const dot = clean.lastIndexOf('.')
  return dot > 0 && dot < clean.length - 1 ? clean.slice(dot + 1).toLowerCase() : ''
}

/** Where a server file opens: `fullPath`, or `path` + `name` as older responses have it */
export const fileUrl = file => {
  if (!file) return ''
  if (file.fullPath) return file.fullPath
  return file.path && file.name ? `${file.path}/${file.name}` : ''
}

export const formatBytes = bytes => {
  const size = Number(bytes)
  if (!Number.isFinite(size) || size <= 0) return ''
  if (size < 1024) return `${size} Б`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} КБ`
  return `${(size / 1024 / 1024).toFixed(1).replace('.0', '')} МБ`
}

/** Why the file cannot be sent, or '' */
export const validateFile = (file, { allowedExtensions = [], maxSizeMb = 0 } = {}) => {
  const name = String((file && file.name) || '').toLowerCase()
  if (allowedExtensions.length && !allowedExtensions.some(ext => name.endsWith(String(ext).toLowerCase()))) {
    return `Неподдерживаемый формат. Допустимо: ${allowedExtensions.join(', ')}`
  }
  if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
    return `Файл больше ${maxSizeMb} МБ (${formatBytes(file.size)})`
  }
  return ''
}

/** The reason of a failed request for the person: the status and the server's message, or that there was no answer */
export const describeUploadError = (error, prefix = 'Не удалось загрузить') => {
  const response = error && error.response
  const lead = prefix ? `${prefix}: ` : ': '
  if (!response) return `${lead}сервер не ответил, проверьте соединение`
  const message = response.data && response.data.message
  const text = Array.isArray(message) ? message.join(', ') : message
  return `${lead}${response.status}${text ? ` — ${text}` : ''}`
}

/**
 * Sends one file to the API (`/file/upload` by default) through the app's axios instance — it signs the request and
 * renews the session. Resolves with the server's file object.
 */
export const uploadFile = async (file, {
  endpoint = '/file/upload', stringifyLastModified = false, library = false, onProgress,
} = {}) => {
  const form = new FormData()
  form.append('file', file)
  const lastModified = file.lastModified || Date.now()
  form.append('lastModified', stringifyLastModified ? String(lastModified) : lastModified)
  // the file is offered for reuse in the admin's file library
  if (library) form.append('library', '1')

  const response = await axios.post(endpoint, form, {
    // the instance's error toast would repeat what the uploader shows itself
    silentError: true,
    onUploadProgress: event => {
      if (onProgress && event.total) onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)))
    },
  })
  return response.data
}
